import _ from 'lodash';
import React from 'react';
import { Table, TableRow, TableRowColumn, TableBody, TableHeaderColumn, TableHeader } from 'material-ui/Table';
import TextField from 'material-ui/TextField';
import IconButton from 'material-ui/IconButton';
import Checkbox from 'material-ui/Checkbox';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import ContentAddIcon from 'material-ui/svg-icons/content/add-circle-outline';
import ContentRemoveIcon from 'material-ui/svg-icons/content/remove-circle-outline';
import NumberField from '../../../Base/NumberField';
import { HEATING_MEDIUM, TANK_COATING_TYPES } from '../../../../constants/vesselRequirements';

function fixPrecisionError(n) {
    if (n === '' || n === null || n === undefined) return n;
    let [int, frac] = n.toFixed(10).split('.');
    frac = ('.' + (frac || '')).replace(/0+$/g, '').replace(/\.$/g, '');
    return parseFloat(int + frac);
}

function PortStarboardTable({
    items, confirmed, field, getError, changeField, modifiedFields, underStyle, caption,
}) {
    const handleAdd = () => {
        const value = _.cloneDeep(items);
        value.push({
            port: '',
            starboard: '',
        });
        changeField({ field, value });
    };
    const handleRemove = (index) => {
        const newItems = _.cloneDeep(items);
        newItems.splice(index, 1);
        changeField({ field, value: newItems });
    };
    return (
        <React.Fragment>
            <Table
                selectable={false}
                style={{ border: '1px solid #DFDFDF' }}
            >
                <TableHeader
                    adjustForCheckbox={false}
                    displaySelectAll={false}
                >
                    <TableRow>
                        <TableHeaderColumn
                            style={{ textAlign: 'center', borderBottomWidth: '2px' }}
                            colSpan={2}
                        >
                            {caption}
                        </TableHeaderColumn>
                    </TableRow>
                    <TableRow>
                        <TableHeaderColumn style={{ textAlign: 'center', paddingLeft: '0', paddingRight: '0' }}>
                            Port, m3
                        </TableHeaderColumn>
                        <TableHeaderColumn style={{ textAlign: 'center', paddingLeft: '0', paddingRight: '0' }}>
                            Starboard, m3
                        </TableHeaderColumn>
                    </TableRow>
                </TableHeader>
            </Table>
            <Table
                selectable={false}
                style={{ border: '1px solid #DFDFDF' }}
            >
                <TableBody displayRowCheckbox={false}>
                    {items.map(({ port, starboard }, index) => (
                        <TableRow key={index}>
                            <TableRowColumn style={{
                                width: '25px', paddingLeft: '0', paddingRight: '0', textAlign: 'center',
                            }}
                            >
                                {index + 1}
                            </TableRowColumn>
                            <TableRowColumn style={{ paddingLeft: '5px', paddingRight: '5px' }}>
                                <NumberField
                                    name={`${field}[${index}].port`}
                                    value={port || ''}
                                    disabled={confirmed}
                                    errorText={getError(`${field}[${index}].port`)}
                                    fullWidth
                                    floatNumber
                                    onChange={(e, value) => {
                                        changeField({
                                            field: `${field}[${index}].port`,
                                            value,
                                            type: 'number',
                                        });
                                    }}
                                    underlineStyle={modifiedFields && modifiedFields.includes(`${field}[${index}].port`) ? underStyle : null}
                                />
                            </TableRowColumn>
                            <TableRowColumn style={{ paddingLeft: '5px', paddingRight: '5px' }}>
                                <NumberField
                                    name={`${field}[${index}].starboard`}
                                    value={starboard || ''}
                                    disabled={confirmed}
                                    errorText={getError(`${field}[${index}].starboard`)}
                                    fullWidth
                                    floatNumber
                                    onChange={(e, value) => {
                                        changeField({
                                            field: `${field}[${index}].starboard`,
                                            value,
                                            type: 'number',
                                        });
                                    }}
                                    underlineStyle={modifiedFields && modifiedFields.includes(`${field}[${index}].starboard`) ? underStyle : null}
                                />
                            </TableRowColumn>
                            <TableRowColumn style={{ width: '50px', paddingLeft: 0, paddingRight: 0 }}>
                                {index === 0 && (
                                    <IconButton
                                        disabled={confirmed}
                                        onClick={handleAdd}
                                    >
                                        <ContentAddIcon color='green' />
                                    </IconButton>
                                )}
                                {index !== 0 && (
                                    <IconButton onClick={() => handleRemove(index)}>
                                        <ContentRemoveIcon color='red' />
                                    </IconButton>
                                )}
                            </TableRowColumn>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </React.Fragment>
    );
}

/** @param {object} props */
function CapacityTanker({
    capacityTanker, confirmed, getError, changeField, modifiedFields, underStyle,
}) {
    const {
        segregation, details, ttlCapacity, cargoGrades, tanks, slops, ballastTanks,
        cargoCapacity, slopsCapacity, ballastCapacity, wholeTank, coatingType, heating,
    } = capacityTanker;
    const handleChangeItem = (change) => {
        changeField(change);
        const c = _.cloneDeep(capacityTanker);
        _.set({ capacityTanker: c }, change.field, change.value);
        if (change.field.startsWith('capacityTanker.tanks')) {
            changeField({
                field: 'capacityTanker.cargoCapacity',
                value: fixPrecisionError(c.tanks.reduce((s, t) => s + (parseFloat(t.port) || 0) + (parseFloat(t.starboard) || 0), 0)),
            });
        } else if (change.field.startsWith('capacityTanker.slops')) {
            changeField({
                field: 'capacityTanker.slopsCapacity',
                value: fixPrecisionError(c.slops.reduce((s, t) => s + (parseFloat(t.port) || 0) + (parseFloat(t.starboard) || 0), 0)),
            });
        } else if (change.field.startsWith('capacityTanker.ballastTanks')) {
            changeField({
                field: 'capacityTanker.ballastCapacity',
                value: fixPrecisionError(c.ballastTanks.reduce((s, t) => s + (parseFloat(t.port) || 0) + (parseFloat(t.starboard) || 0), 0)),
            });
        }
    };
    return (
        <React.Fragment>
            <div className="row">
                <div className='col-xs-3'>
                    <NumberField
                        name='capacityTanker.segregation'
                        floatingLabelText='Segregation, number'
                        value={segregation || ''}
                        disabled={confirmed}
                        errorText={getError('capacityTanker.segregation')}
                        fullWidth
                        floatNumber
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.segregation',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.segregation') ? underStyle : null}
                    />
                </div>
                <div className='col-xs-3'>
                    <TextField
                        name='capacityTanker.details'
                        floatingLabelText='Details'
                        value={details || ''}
                        disabled={confirmed}
                        errorText={getError('capacityTanker.details')}
                        fullWidth
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.details',
                                value,
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.details') ? underStyle : null}
                    />
                </div>
                <div className='col-xs-3'>
                    <NumberField
                        name='capacityTanker.ttlCapacity'
                        floatingLabelText='TTL capacity (98%), m3'
                        value={ttlCapacity || ''}
                        disabled={confirmed}
                        errorText={getError('capacityTanker.ttlCapacity')}
                        fullWidth
                        floatNumber
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.ttlCapacity',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.ttlCapacity') ? underStyle : null}
                    />
                </div>
                <div className='col-xs-3'>
                    <NumberField
                        name='capacityTanker.cargoGrades'
                        floatingLabelText='Cargo grades, num'
                        value={cargoGrades || ''}
                        disabled={confirmed}
                        errorText={getError('capacityTanker.cargoGrades')}
                        fullWidth
                        floatNumber
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.cargoGrades',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.cargoGrades') ? underStyle : null}
                    />
                </div>
            </div>
            <div className="row">
                <div className='col-xs-4'>
                    <PortStarboardTable
                        items={tanks || []}
                        field="capacityTanker.tanks"
                        caption='Tanks'
                        confirmed={confirmed}
                        getError={getError}
                        changeField={handleChangeItem}
                        modifiedFields={modifiedFields}
                        underStyle={underStyle}
                    />
                    <NumberField
                        name="capacityTanker.cargoCapacity"
                        floatingLabelText='Cargo capacity (98%), m3'
                        value={cargoCapacity || ''}
                        disabled={confirmed}
                        errorText={getError('capacityTanker.cargoCapacity')}
                        fullWidth
                        floatNumber
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.cargoCapacity',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.cargoCapacity') ? underStyle : null}
                    />
                </div>
                <div className='col-xs-4'>
                    <PortStarboardTable
                        items={slops || []}
                        field="capacityTanker.slops"
                        caption='Slops'
                        confirmed={confirmed}
                        getError={getError}
                        changeField={handleChangeItem}
                        modifiedFields={modifiedFields}
                        underStyle={underStyle}
                    />
                    <NumberField
                        name="capacityTanker.slopsCapacity"
                        floatingLabelText='Slops capacity (98%), m3'
                        value={slopsCapacity || ''}
                        disabled={confirmed}
                        errorText={getError('capacityTanker.slopsCapacity')}
                        fullWidth
                        floatNumber
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.slopsCapacity',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.slopsCapacity') ? underStyle : null}
                    />
                </div>
                <div className='col-xs-4'>
                    <PortStarboardTable
                        items={ballastTanks || []}
                        field="capacityTanker.ballastTanks"
                        caption='Ballast tanks'
                        confirmed={confirmed}
                        getError={getError}
                        changeField={handleChangeItem}
                        modifiedFields={modifiedFields}
                        underStyle={underStyle}
                    />
                    <NumberField
                        name="capacityTanker.ballastCapacity"
                        floatingLabelText='Ballast capacity (98%), m3'
                        value={ballastCapacity || ''}
                        disabled={confirmed}
                        errorText={getError('capacityTanker.ballastCapacity')}
                        fullWidth
                        floatNumber
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.ballastCapacity',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.ballastCapacity') ? underStyle : null}
                    />
                </div>
            </div>
            <div className='row'>
                <div className='col-xs-3'>
                    <div className='name-block huge-name' style={{ margin: '2em 0 0' }}>
                        TANK COATING
                    </div>
                </div>
            </div>
            <div className='row'>
                <div className='col-xs-3'>
                    <Checkbox
                        label='Whole Tank'
                        checked={!!wholeTank}
                        onCheck={(e, value) => changeField({
                            field: 'capacityTanker.wholeTank',
                            value,
                        })}
                        style={{ marginTop: '36px' }}
                    />
                </div>
                <div className='col-xs-3'>
                    <SelectField
                        value={coatingType}
                        disabled={confirmed}
                        onChange={(e, i, value) => {
                            changeField({
                                field: 'capacityTanker.coatingType',
                                value,
                            });
                        }}
                        maxHeight={200}
                        name='capacityTanker.coatingType'
                        floatingLabelText='Type'
                        fullWidth
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.coatingType') ? underStyle : null}
                    >
                        {
                            ['', ...TANK_COATING_TYPES].map(name => (
                                <MenuItem
                                    key={name}
                                    value={name}
                                    primaryText={name}
                                />
                            ))
                        }
                    </SelectField>
                </div>
            </div>
            <div className='row'>
                <div className='col-xs-3'>
                    <div className='name-block huge-name' style={{ margin: '2em 0 0' }}>
                        HEATING
                    </div>
                </div>
            </div>
            <div className='row'>
                <div className='col-xs-3'>
                    <SelectField
                        value={heating.exchanger}
                        disabled={confirmed}
                        onChange={(e, i, value) => {
                            changeField({
                                field: 'capacityTanker.heating.exchanger',
                                value,
                            });
                        }}
                        maxHeight={200}
                        name='capacityTanker.heating.exchanger'
                        floatingLabelText='Heat Exchanger'
                        fullWidth
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.heating.exchanger') ? underStyle : null}
                    >
                        <MenuItem value={null} primaryText={undefined} />
                        <MenuItem value primaryText='Yes' />
                        <MenuItem value={false} primaryText='No' />
                    </SelectField>
                </div>
                <div className='col-xs-3'>
                    <SelectField
                        value={heating.medium}
                        disabled={confirmed || !heating.exchanger}
                        onChange={(e, i, value) => {
                            changeField({
                                field: 'capacityTanker.heating.medium',
                                value,
                            });
                        }}
                        maxHeight={200}
                        name='capacityTanker.heating.medium'
                        floatingLabelText='Heating medium'
                        fullWidth
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.heating.medium') ? underStyle : null}
                    >
                        {
                            ['', ...HEATING_MEDIUM].map(name => (
                                <MenuItem
                                    key={name}
                                    value={name}
                                    primaryText={name}
                                />
                            ))
                        }
                    </SelectField>
                </div>
                <div className='col-xs-3'>
                    <NumberField
                        name="capacityTanker.heating.maxLoaded"
                        floatingLabelText='Max loaded °C'
                        value={heating.maxLoaded}
                        disabled={confirmed || !heating.exchanger}
                        errorText={getError('capacityTanker.heating.maxLoaded')}
                        fullWidth
                        floatNumber
                        canBeNegative
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.heating.maxLoaded',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.heating.maxLoaded') ? underStyle : null}
                    />
                </div>
                <div className='col-xs-3'>
                    <NumberField
                        name="capacityTanker.heating.maxMaintained"
                        floatingLabelText='Max maintained °C'
                        value={heating.maxMaintained}
                        disabled={confirmed || !heating.exchanger}
                        errorText={getError('capacityTanker.heating.maxMaintained')}
                        fullWidth
                        floatNumber
                        canBeNegative
                        onChange={(e, value) => {
                            changeField({
                                field: 'capacityTanker.heating.maxMaintained',
                                value,
                                type: 'number',
                            });
                        }}
                        underlineStyle={modifiedFields && modifiedFields.includes('capacityTanker.heating.maxMaintained') ? underStyle : null}
                    />
                </div>
            </div>
        </React.Fragment>
    );
}

export default CapacityTanker;



// WEBPACK FOOTER //
// ./app/components/Vessels/Single/Rows/CapacityTanker.jsx